import React, { useEffect, useRef, useState } from 'react';
import { Flex, Form, Modal, Popover } from 'antd';
import { t } from 'i18next';
import Title from 'antd/es/typography/Title';
import DataTotalTable from './DataTotalTable';
import { Brief, DataTotal, Period } from '../../../../api/types';
import ButtonBlack from '../../../ui/buttons/ButtonBlack';
import OptimizationForm from './OptimizationForm';
import IconDoneOutlined from '../../../ui/icons/IconDoneOutlined';
import SwitchGreen from '../../../ui/inputs/SwitchGreen';
import UploadData from './UploadData';
import { TriggerWithArgs } from 'swr/mutation';
import Roles from '../../../../roles/Roles';
import DataTotalsFilter from './filter/DataTotalsFilter';
import { KeyedMutator } from 'swr';
import { DataTotalsFilterInterface } from './useDataTotals';
import HistoricDataTotalTable from './historic/HistoricDataTotalTable';
import IconCompareSettings from '../../../ui/icons/IconCompareSettings';
import TableMetricsFilter from './filter/TableMetricsFilter';
import { TableMetrics } from '../dataTotals/filter/TableMetricsFilter';

interface DataTotalsProps {
  period: Period,
  dataTotals: DataTotal[],
  setDataTotalsFilter: Function,
  isDataTotalsLoading: boolean,
  isDataTotalLoading: boolean,
  brief: Brief,
  selectedPlanId: number,
  setSelectedPlanId: Function,
  mutateDataTotal: TriggerWithArgs<{
    data: DataTotal
  }, any, string, number>,
  mutateDataTotals: KeyedMutator<{ data: DataTotal[]; }>,
  selectedForComparesIds: string[],
  setSelectedForCompares: Function,
  showCompare: boolean,
  showFactData: boolean,
  isFactData: boolean,
  setShowCompare: Function,
  setShowFactData: Function,
  setShowFactTotal: Function,
  setHistoricData: Function,
  dataTotalsFilter: DataTotalsFilterInterface,
}

const isElementInViewport = (el: HTMLElement | null) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};
export default function DataTotals ({
  period, brief, selectedPlanId, setSelectedPlanId, isDataTotalsLoading,
  selectedForComparesIds, setSelectedForCompares, showCompare, setShowCompare,
  dataTotals, setShowFactTotal, mutateDataTotal, setDataTotalsFilter, showFactData,
  setShowFactData, isFactData, mutateDataTotals, isDataTotalLoading, dataTotalsFilter,
  setHistoricData,
}: DataTotalsProps) {
  const [isOptimizationModalOpen, setOptimizationModalOpen] = useState<boolean>(false);
  const [canEditForm, setCanEditForm] = useState<boolean>(false);
  const [currentPlanId, setCurrentPlanId] = useState<number>(0);
  const [optimizationForm] = Form.useForm();
  const [optimizationFormKey, setOptimizationFormKey] = useState(0);
  const planRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [selectedMetrics, setSelectedMetrics] = useState<Array<keyof TableMetrics>>([
    'kpi', 'kpi_short_term', 'kpi_long_term', 'cumulative', 'budget', 'roas', 'audienceText', 'managerText'
  ]);

  const isPeriodAccepted = () => {
    return dataTotals && dataTotals.length > 0 && dataTotals.some((dataTotal: DataTotal) => dataTotal.isAccepted);
  };

  useEffect(() => {
    if (isOptimizationModalOpen) {
      setOptimizationFormKey(prev => prev + 1);
    }
  }, [isOptimizationModalOpen]);

  const scrollToBlock = (dataTotalId: number) => {
    setTimeout(() => {
      const element = planRefs.current[dataTotalId];

      if (element) {
        if (!isElementInViewport(element)) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }

        element.classList.add('highlight');

        setTimeout(() => {
          element.classList.remove('highlight');
        }, 2000);
      }
    }, 500);
  };

  return (
    <Flex vertical gap={'middle'} justify={'center'}>
      <Flex justify={'space-between'}>
        <Title level={4} className={'planner__block-title'}>{t('planner.forecasts')}</Title>
        <Flex gap={'large'} align={'center'}>
          <Flex gap={6} align={'center'}>
            <SwitchGreen
              size={'small'}
              value={showFactData}
              onChange={value => {
                setShowFactData(value);
                setShowFactTotal(value);
              }}
            />
            <Flex>{t('planner.historicalData')}</Flex>
          </Flex>
          {!isDataTotalsLoading &&
            Roles.hasAccess('canUploadPlan') &&
            <UploadData
              period={period}
              dataTotals={dataTotals}
              scrollToBlock={scrollToBlock}
              mutateDataTotal={mutateDataTotal}
              setShowHistoricalData={setShowFactData}
              isFactData={isFactData}
            />
          }

          <>
            {!isPeriodAccepted() && !isDataTotalsLoading &&
              Roles.hasAccess('canCreateOptimization') &&
              <>
                <ButtonBlack onClick={() => {
                  setOptimizationModalOpen(true);
                  setCanEditForm(true);
                }}>{t('planner.createOptimization')}</ButtonBlack>
                <Modal
                  title={t('planner.newOptimization')}
                  open={isOptimizationModalOpen}
                  footer={null}
                  onCancel={() => {
                    setOptimizationModalOpen(false);
                  }}
                  width={964}
                  afterClose={() => {
                    setCurrentPlanId(0);
                    setCanEditForm(false);
                    optimizationForm.setFieldValue('budgetLimit', 0);
                    optimizationForm.setFieldValue('kpiLimit', 0);
                  }}
                >
                  {isOptimizationModalOpen &&
                    <div
                      key={optimizationFormKey}
                    >
                      <OptimizationForm
                        dataTotalId={currentPlanId}
                        setDataTotalId={setCurrentPlanId}
                        afterSave={() => {
                          setOptimizationModalOpen(false);
                        }}
                        editable={canEditForm}
                        brief={brief}
                        period={period}
                        form={optimizationForm}
                        scrollToBlock={scrollToBlock}
                        mutateDataTotal={mutateDataTotal}
                      />
                    </div>
                  }
                </Modal>
              </>
            }

            {selectedForComparesIds.length > 1 && !showCompare &&
              <ButtonBlack onClick={() => {
                setShowCompare(true);
              }} icon={<IconDoneOutlined/>} style={{
                display: 'flex',
                alignItems: 'center'
              }}>{t('planner.compare')} {`(+${selectedForComparesIds.length})`}</ButtonBlack>
            }
          </>
        </Flex>
      </Flex>
      <Flex justify={'space-between'} gap={'large'}>
        <Popover placement="bottomLeft" content={
          <div className={'compare__filter-popover'}>
            <TableMetricsFilter
              selectedMetrics={selectedMetrics}
              setSelectedMetrics={setSelectedMetrics}
            />
          </div>
        } arrow={false}>
          <Flex align={'center'} gap={'small'} className={'planner__settings-button'}>
            <IconCompareSettings/>
            {t('planner.setView')}
          </Flex>
        </Popover>
        <DataTotalsFilter
          setDataTotalsFilter={setDataTotalsFilter}
        />
      </Flex>

      <Flex vertical gap={32}>
        {brief?.engine?.id && showFactData &&
          <HistoricDataTotalTable
            engineId={brief?.engine?.id}
            period={period}
            brief={brief}
            filter={dataTotalsFilter}
            selectedForComparesIds={selectedForComparesIds}
            setSelectedForCompares={setSelectedForCompares}
            setHistoricData={setHistoricData}
            selectedMetrics={selectedMetrics}
          />
        }


        {dataTotals.map((dataTotal: DataTotal) => (
          <div key={dataTotal.id}>
            <DataTotalTable
              ref={el => planRefs.current[dataTotal.id] = el}
              dataTotal={dataTotal}
              mutateDataTotals={mutateDataTotals}
              mutateDataTotal={mutateDataTotal}
              isDataTotalsLoading={isDataTotalsLoading}
              isDataTotalLoading={isDataTotalLoading}
              isPeriodAccepted={isPeriodAccepted()}
              selectedPlanId={selectedPlanId}
              setSelectedPlanId={setSelectedPlanId}
              setCurrentPlanId={setCurrentPlanId}
              showForm={() => setOptimizationModalOpen(true)}
              filter={[]}
              selectedForComparesIds={selectedForComparesIds}
              setSelectedForCompares={setSelectedForCompares}
              setShowFactData={setShowFactData}
              selectedMetrics={selectedMetrics}
            />
          </div>
        ))}
      </Flex>
    </Flex>
  );
}
